import { useCallback, useEffect, useRef, useState } from "react";
import { isRequestCancellation } from "@shared/api/client";
import { getTasksData } from "../api/tasks";
import type { Task, TaskMeta } from "../types/tasks";

const emptyMeta: TaskMeta = {
  current_user: {
    id: 0,
    nombre: "Usuario",
    correo: "",
    rol: "empleado",
  },
  users: [],
  projects: [],
  permissions: {
    can_create: false,
    can_reorder: false,
    can_assign: false,
    can_delete: false,
  },
};

export function useTasks() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [meta, setMeta] = useState<TaskMeta>(emptyMeta);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  const reload = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getTasksData();

      if (!mountedRef.current) return;

      setTasks(response.data);
      setMeta(response.meta);
    } catch (error) {
      if (!isRequestCancellation(error)) {
        console.error("Error cargando tareas", error);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void reload();

    return () => {
      mountedRef.current = false;
    };
  }, [reload]);

  const replaceTask = useCallback((task: Task) => {
    setTasks((current) => {
      const index = current.findIndex((item) => item.id === task.id);

      if (index === -1) {
        return [task, ...current];
      }

      const next = [...current];
      next[index] = task;
      return next;
    });
  }, []);

  return {
    tasks,
    meta,
    loading,
    reload,
    setTasks,
    replaceTask,
  };
}
